import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart3, Users, ChevronLeft, ChevronRight, Lock, Unlock, Loader2, Calendar,
} from 'lucide-react';
import AdminShell from '../components/AdminShell';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatHour = (h) => {
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display}:00 ${suffix}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];
const shiftDate = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const AdminAppointmentSlots = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayISO());
  const [serviceType, setServiceType] = useState('digimitra');
  const [data, setData] = useState({ slots: [], bookings: [] });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchView = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) { navigate('/admin/login'); return; }
      const res = await axios.get(`${API}/admin/appointments/view`, {
        params: { date, service_type: serviceType },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load slots');
      }
    } finally {
      setLoading(false);
    }
  }, [date, serviceType, navigate]);

  useEffect(() => { fetchView(); }, [fetchView]);

  const toggleSlot = async (slot, is_open) => {
    setUpdating(slot.hour);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/appointments/slot-override`,
        { date, hour: slot.hour, service_type: serviceType, is_open, capacity: slot.capacity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${formatHour(slot.hour)} ${is_open ? 'opened' : 'closed'}`);
      await fetchView();
    } catch {
      toast.error('Failed to update slot');
    } finally {
      setUpdating(null);
    }
  };

  const updateCapacity = async (slot, capacity) => {
    const parsed = parseInt(capacity);
    if (isNaN(parsed) || parsed < 0) return;
    if (parsed === slot.capacity) return;
    setUpdating(slot.hour);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/appointments/slot-override`,
        { date, hour: slot.hour, service_type: serviceType, is_open: slot.is_open, capacity: parsed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchView();
    } catch {
      toast.error('Failed to update capacity');
    } finally {
      setUpdating(null);
    }
  };

  const totalBooked = data.slots.reduce((a, s) => a + (s.booked || 0), 0);
  const openCount = data.slots.filter((s) => s.is_open).length;

  return (
    <AdminShell>
      <SEO title="Appointment Slots - Elderra Admin" description="Manage hourly appointment slots." />

      {/* Header */}
      <div data-testid="admin-appointments-page" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Scheduling</div>
        <h1 style={{ margin: '4px 0 6px', fontSize: 32, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>Appointment Slots</h1>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>Open or close hourly slots. Default: all open, capacity 5.</p>
      </div>

      {/* Controls */}
      <div data-testid="apt-controls" style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
        <div data-testid="apt-service-toggle" style={{ display: 'inline-flex', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 4 }}>
          {[
            { id: 'digimitra', label: 'DigiMitra', Icon: BarChart3 },
            { id: 'youtuber', label: 'YouTuber', Icon: Users },
          ].map(({ id, label, Icon }) => {
            const active = serviceType === id;
            return (
              <button
                key={id}
                data-testid={`apt-svc-${id}`}
                onClick={() => setServiceType(id)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', border: 'none',
                  background: active ? '#0f172a' : 'transparent',
                  color: active ? '#fff' : '#6b7280',
                  borderRadius: 7, cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                <Icon size={15} /> {label}
              </button>
            );
          })}
        </div>

        <div data-testid="apt-date-nav" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setDate(shiftDate(date, -1))} data-testid="apt-prev-day" style={{ padding: 9, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 9, cursor: 'pointer', display: 'inline-flex' }}>
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="apt-date-input"
            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, fontWeight: 600, background: '#fff', fontFamily: 'inherit' }}
          />
          <button onClick={() => setDate(shiftDate(date, 1))} data-testid="apt-next-day" style={{ padding: 9, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 9, cursor: 'pointer', display: 'inline-flex' }}>
            <ChevronRight size={16} />
          </button>
          <button onClick={() => setDate(todayISO())} data-testid="apt-today-btn" style={{ padding: '8px 12px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
            Today
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}><Loader2 size={20} style={{ animation: 'adm-spin 1s linear infinite' }} /> Loading...</div>
      ) : (
        <>
          {/* Summary strip */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Open slots', value: openCount, hint: `of ${data.slots.length}` },
              { label: 'Total booked', value: totalBooked, hint: 'today' },
              { label: 'Service', value: serviceType === 'digimitra' ? 'DigiMitra' : 'YouTuber', hint: 'viewing' },
            ].map((s) => (
              <div key={s.label} style={{ background: '#fff', padding: '14px 18px', borderRadius: 12, border: '1px solid #e5e7eb', minWidth: 140 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{s.value} <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{s.hint}</span></div>
              </div>
            ))}
          </div>

          {/* Slot cards */}
          <div data-testid="apt-slots-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 30 }}>
            {data.slots.map((slot) => {
              const isFull = slot.is_open && slot.booked >= slot.capacity;
              const badgeBg = !slot.is_open ? '#fee2e2' : isFull ? '#fef3c7' : '#dcfce7';
              const badgeFg = !slot.is_open ? '#991b1b' : isFull ? '#92400e' : '#166534';
              const badgeLabel = !slot.is_open ? 'Closed' : isFull ? 'Full' : 'Open';
              return (
                <div
                  key={slot.hour}
                  data-testid={`apt-card-${slot.hour}`}
                  style={{
                    background: '#fff', border: `1.5px ${slot.is_open ? 'solid' : 'dashed'} ${isFull ? '#fbbf24' : '#e5e7eb'}`,
                    borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
                    opacity: slot.is_open ? 1 : 0.85, transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>{formatHour(slot.hour)}</div>
                    <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: badgeBg, color: badgeFg }}>
                      {badgeLabel}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Booked</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{slot.booked} / {slot.capacity}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Capacity</div>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        defaultValue={slot.capacity}
                        onBlur={(e) => updateCapacity(slot, e.target.value)}
                        data-testid={`apt-capacity-${slot.hour}`}
                        style={{ width: 54, padding: '3px 6px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, fontWeight: 600, textAlign: 'center', fontFamily: 'inherit' }}
                      />
                    </div>
                  </div>
                  <button
                    disabled={updating === slot.hour}
                    onClick={() => toggleSlot(slot, !slot.is_open)}
                    data-testid={`apt-toggle-${slot.hour}`}
                    style={{
                      padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 9, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      background: slot.is_open ? '#fff' : '#0f172a',
                      color: slot.is_open ? '#991b1b' : '#fff',
                      transition: 'all 0.15s',
                    }}
                  >
                    {updating === slot.hour ? <Loader2 size={14} style={{ animation: 'adm-spin 1s linear infinite' }} /> : slot.is_open ? <><Lock size={13} /> Close slot</> : <><Unlock size={13} /> Open slot</>}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Bookings list */}
          <div data-testid="apt-bookings-list" style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Bookings on {date}</h2>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9ca3af' }}>
                <Calendar size={12} /> {data.bookings.length} total
              </span>
            </div>
            {data.bookings.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No bookings for this date yet.</p>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 140px 100px', gap: 12, padding: '8px 4px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6' }}>
                  <span>Time</span><span>Name</span><span>Phone</span><span>Status</span>
                </div>
                {data.bookings
                  .slice()
                  .sort((a, b) => (a.hour ?? 99) - (b.hour ?? 99))
                  .map((b) => {
                    const statusColor = {
                      pending: '#d97706', confirmed: '#16a34a',
                      completed: '#2563eb', cancelled: '#dc2626',
                    }[b.status] || '#6b7280';
                    return (
                      <div key={b.id} data-testid={`apt-booking-${b.id}`} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 140px 100px', gap: 12, padding: '12px 4px', fontSize: 13, borderBottom: '1px solid #f9fafb' }}>
                        <span style={{ fontWeight: 600 }}>{b.hour != null ? formatHour(b.hour) : '—'}</span>
                        <span>{b.name}</span>
                        <span style={{ color: '#6b7280' }}>{b.phone}</span>
                        <span style={{ color: statusColor, fontWeight: 600, textTransform: 'capitalize' }}>{b.status}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </>
      )}

      <style>{`@keyframes adm-spin { to { transform: rotate(360deg); } }`}</style>
    </AdminShell>
  );
};

export default AdminAppointmentSlots;
